import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar, Search, RefreshCw, Building2, User,
  AlertTriangle, X, ChevronRight, Clock, CheckCircle2,
  AlertCircle, ExternalLink, Zap, Send, Inbox,
  MessageSquare, Timer, ShieldCheck, Loader
} from 'lucide-react';
import KairaliAIIcon from './KairaliAIIcon';
import { useCachedData } from '../hooks/useCachedData';
import { useStickyFilter } from '../hooks/useStickyFilter';

// ─── Shared helpers ──────────────────────────────────────────────────────────

const PRIORITY = {
  HIGH:   { color: 'var(--c-danger)',  bg: 'var(--c-danger-bg)',  label: 'High',   Icon: Zap },
  MEDIUM: { color: 'var(--c-warning)', bg: 'var(--c-warning-bg)', label: 'Medium', Icon: AlertCircle },
  LOW:    { color: 'var(--c-success)', bg: 'var(--c-success-bg)', label: 'Low',    Icon: ShieldCheck },
};
const getPriority = (level) =>
  PRIORITY[level?.toUpperCase()] ?? { color: 'var(--text-muted)', bg: 'var(--bg-surface-2)', label: level || '—', Icon: AlertCircle };

const fmtDate = (s) => {
  if (!s) return null;
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

// ─── Ticket lifecycle timeline (inside modal) ─────────────────────────────────

const TicketTimeline = ({ task }) => {
  const stages = [
    { label: 'Raised',       date: fmtDate(task.timestamp),     done: !!task.timestamp },
    { label: 'Reply Due',    date: fmtDate(task.replyPlanned),  done: !!task.replyPlanned },
    { label: 'Replied',      date: fmtDate(task.replyActual),   done: !!task.replyActual },
    { label: 'Resolved',     date: fmtDate(task.resolveActual), done: !!task.resolveActual },
  ];
  const doneCount = stages.filter(s => s.done).length;
  const pct = ((doneCount - 1) / (stages.length - 1)) * 100;

  return (
    <div style={{ padding: '16px 20px 20px', background: 'var(--bg-surface-2)', borderRadius: 14, border: '1px solid var(--border)', marginBottom: 20 }}>
      <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '0 0 18px' }}>
        Ticket Lifecycle
      </p>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
        {/* Track base */}
        <div style={{ position: 'absolute', top: 15, left: '12.5%', right: '12.5%', height: 2, background: 'var(--border)', borderRadius: 2 }} />
        {/* Track fill */}
        <div style={{
          position: 'absolute', top: 15, left: '12.5%', height: 2,
          width: `${Math.max(0, Math.min(75, pct * 0.75))}%`,
          background: 'linear-gradient(90deg, var(--green-primary), var(--green-light))',
          borderRadius: 2, transition: 'width 0.7s ease',
        }} />
        {stages.map((stage, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', marginBottom: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: stage.done ? 'var(--green-primary)' : 'var(--bg-surface)',
              border: `2px solid ${stage.done ? 'var(--green-primary)' : 'var(--border)'}`,
              boxShadow: stage.done ? '0 0 0 4px var(--green-dim)' : 'none',
              transition: 'all 0.3s ease',
            }}>
              {stage.done
                ? <CheckCircle2 size={14} style={{ color: '#fff' }} />
                : <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border)' }} />}
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: stage.done ? 'var(--text-primary)' : 'var(--text-muted)', textAlign: 'center', marginBottom: 3, lineHeight: 1.2 }}>
              {stage.label}
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.3 }}>
              {stage.date || '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Info block (used in modal grid) ─────────────────────────────────────────

const InfoBlock = ({ label, value, accent }) => (
  <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border)' }}>
    <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', margin: '0 0 5px' }}>{label}</p>
    <p style={{ fontSize: 13, fontWeight: 600, color: accent || 'var(--text-primary)', margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word', lineHeight: 1.4 }}>
      {value || '—'}
    </p>
  </div>
);

// ─── Solution block ───────────────────────────────────────────────────────────

const SolutionBlock = ({ label, value, accent }) => (
  <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border)', borderLeft: `3px solid ${accent}` }}>
    <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: accent, margin: '0 0 5px' }}>{label}</p>
    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', margin: 0, overflowWrap: 'break-word', lineHeight: 1.5 }}>{value}</p>
  </div>
);

// ─── Task Detail Modal ────────────────────────────────────────────────────────

const TaskDetailModal = ({ task, onClose, currentUser, clickedReplyRows, replyPanel, onOpenReply, onCloseReply, onSubmitReply, onAttachReply }) => {
  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    const origPadding  = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
    const onEsc = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = origOverflow;
      document.body.style.paddingRight = origPadding;
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const p = getPriority(task.challengeLevel);
  const PIcon = p.Icon;
  const needsReply   = task.issueDelegatedTo === currentUser.name && !task.recommendation?.trim();
  const needsResolve = task.name === currentUser.name && task.resolvePlanned?.trim() && !task.resolveActual?.trim() && task.resolveLink;

  return createPortal(
    <div className="k-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="k-modal" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="k-modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 800, color: 'var(--gold-light)', letterSpacing: '0.5px' }}>
                {task.ticketId}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${p.color}22`, color: p.color, border: `1px solid ${p.color}50` }}>
                <PIcon size={11} /> {p.label} Priority
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(212,168,64,0.18)', color: 'var(--gold-light)', border: '1px solid rgba(212,168,64,0.35)' }}>
                {task.issueDelegatedTo === currentUser.name ? 'Delegated to You' : 'Raised by You'}
              </span>
              {task.replyActual?.trim() && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(92,175,128,0.2)', color: '#A0E0B8', border: '1px solid rgba(92,175,128,0.35)' }}>
                  <CheckCircle2 size={11} /> Replied
                </span>
              )}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              {task.name} · {task.department} · Raised {fmtDate(task.timestamp)}
            </p>
          </div>
          <button className="k-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="k-modal-body">

          {/* Lifecycle */}
          <TicketTimeline task={task} />

          {/* Challenge/Issue */}
          <div>
            <p className="k-section-title">Challenge / Issue</p>
            <div className="k-remarks" style={{ fontSize: 14, lineHeight: 1.7 }}>
              {task.challengeIssue || 'No description provided'}
            </div>
          </div>

          {/* Solutions */}
          {(task.solution1 || task.solution2 || task.solution3) && (
            <div>
              <p className="k-section-title">Solutions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {task.solution1 && <SolutionBlock label="Solution 1 — Best Choice" value={task.solution1} accent="var(--c-success)" />}
                {task.solution2 && <SolutionBlock label="Solution 2" value={task.solution2} accent="var(--gold-primary)" />}
                {task.solution3 && <SolutionBlock label="Solution 3" value={task.solution3} accent="var(--teal-primary)" />}
              </div>
            </div>
          )}

          {/* Recommendation */}
          {task.recommendation && (
            <div>
              <p className="k-section-title">Recommendation</p>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--green-primary)' }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', margin: 0, overflowWrap: 'break-word', lineHeight: 1.6 }}>{task.recommendation}</p>
              </div>
            </div>
          )}

          {/* Info grid */}
          <div>
            <p className="k-section-title">Ticket Information</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
              <InfoBlock label="Ticket ID"      value={task.ticketId}                accent="var(--gold-primary)" />
              <InfoBlock label="Raised By"      value={task.name} />
              <InfoBlock label="Email"          value={task.emailId} />
              <InfoBlock label="Department"     value={task.department} />
              <InfoBlock label="Delegated To"   value={task.issueDelegatedTo} />
              <InfoBlock label="Priority"       value={p.label}                     accent={p.color} />
              <InfoBlock label="Reply Planned"  value={fmtDate(task.replyPlanned)} />
              <InfoBlock label="Reply Actual"   value={fmtDate(task.replyActual)}   accent={task.replyActual ? 'var(--c-success)' : undefined} />
              {task.replyTimeDelay && (
                <InfoBlock label="Reply Delay"  value={task.replyTimeDelay}         accent={parseFloat(task.replyTimeDelay) > 0 ? 'var(--c-danger)' : 'var(--c-success)'} />
              )}
              <InfoBlock label="Resolve Planned" value={fmtDate(task.resolvePlanned)} />
              <InfoBlock label="Resolve Actual"  value={fmtDate(task.resolveActual)} accent={task.resolveActual ? 'var(--c-success)' : undefined} />
              {task.resolveTimeDelay && (
                <InfoBlock label="Resolve Delay" value={task.resolveTimeDelay}       accent={parseFloat(task.resolveTimeDelay) > 0 ? 'var(--c-danger)' : 'var(--c-success)'} />
              )}
            </div>
          </div>

          {/* Attachment */}
          {task.attachment && (
            <div>
              <p className="k-section-title">Attachment</p>
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', margin: 0, overflowWrap: 'break-word' }}>{task.attachment}</p>
              </div>
            </div>
          )}

          {/* Resolution */}
          {(task.problemResolved || task.remarks || task.finalDoer) && (
            <div>
              <p className="k-section-title">Resolution Details</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                {task.finalDoer        && <InfoBlock label="Final Doer"        value={task.finalDoer} />}
                {task.problemResolved  && <InfoBlock label="Problem Resolved"   value={task.problemResolved} accent="var(--c-success)" />}
                {task.remarks          && <InfoBlock label="Remarks"            value={task.remarks} />}
                {task.finalTimestamp   && <InfoBlock label="Closed On"          value={fmtDate(task.finalTimestamp)} />}
              </div>
            </div>
          )}

          {/* Action — Reply Required */}
          {needsReply && (
            <div style={{ borderRadius: 12, border: `1px solid ${replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'var(--c-success)' : 'var(--c-danger)'}`, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ padding: '14px 16px', background: replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'var(--c-success-bg)' : 'var(--c-danger-bg)', borderLeft: `4px solid ${replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'var(--c-success)' : 'var(--c-danger)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <AlertCircle size={14} style={{ color: replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'var(--c-success)' : 'var(--c-danger)', flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>
                      {replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'Reply Submitted' : 'Reply Required'}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                      {replyPanel?.submitted || clickedReplyRows.has(task.id) ? 'Your response has been saved to the sheet' : 'This ticket is waiting for your response'}
                    </p>
                  </div>
                </div>
                {!replyPanel?.submitted && !clickedReplyRows.has(task.id) && !replyPanel?.open && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                    <button
                      className="k-btn k-btn-danger"
                      style={{ fontSize: 12, padding: '6px 14px' }}
                      onClick={() => onOpenReply(task.id)}
                      title="Type a quick reply inline — submits directly to the Google Form"
                    >
                      <Send size={12} /> Reply Inline
                    </button>
                    <button
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 10,
                        color: 'var(--text-muted)', background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer',
                      }}
                      onClick={() => onAttachReply(task)}
                      title="Opens the Google Form in a new tab — use when you need to attach a file"
                    >
                      <ExternalLink size={12} /> Reply with Attachment ↗
                    </button>
                  </div>
                )}
              </div>
              {/* Inline panel */}
              {replyPanel?.open && !replyPanel?.submitted && (
                <ReplyPanel
                  task={task}
                  onSubmit={onSubmitReply}
                  onCancel={() => onCloseReply(task.id)}
                  submitting={replyPanel?.submitting}
                  error={replyPanel?.error}
                  submitted={replyPanel?.submitted}
                />
              )}
            </div>
          )}

          {/* Action — Resolve Required */}
          {needsResolve && (
            <div style={{ padding: '16px', borderRadius: 12, background: 'var(--c-warning-bg)', border: '1px solid var(--c-warning)', borderLeft: '4px solid var(--gold-primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Timer size={14} style={{ color: 'var(--gold-primary)', flexShrink: 0 }} />
                  <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Resolution Pending</p>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>Acknowledge or reject the resolution provided</p>
              </div>
              <button
                className="k-btn k-btn-gold"
                onClick={(e) => { e.stopPropagation(); window.open(task.resolveLink, '_blank'); onClose(); }}
                style={{ flexShrink: 0 }}
              >
                <ExternalLink size={13} /> Resolve
              </button>
            </div>
          )}

        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Working-hours delay counter ─────────────────────────────────────────────

function workingMinsBetween(start, end) {
  if (end <= start) return 0;
  let total = 0;
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= endDay) {
    const day = cur.getDay(); // 0=Sun
    if (day !== 0) {
      const ws = new Date(cur); ws.setHours(9, 0, 0, 0);
      const we = new Date(cur); we.setHours(18, 0, 0, 0);
      const from = Math.max(start.getTime(), ws.getTime());
      const to   = Math.min(end.getTime(), we.getTime());
      if (to > from) total += (to - from) / 60000;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return Math.round(total);
}

function fmtWorkingTime(mins) {
  if (mins < 1)  return '0m';
  if (mins < 60) return `${mins}m`;
  const totalH = Math.floor(mins / 60);
  const m      = mins % 60;
  if (totalH < 9) return m > 0 ? `${totalH}h ${m}m` : `${totalH}h`;
  const days = Math.floor(totalH / 9);
  const remH = totalH % 9;
  return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
}

const DelayCounter = ({ timestamp }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const raised = new Date(timestamp);
  if (isNaN(raised.getTime())) return null;

  const graceEnd = new Date(raised.getTime() + 4 * 60 * 60 * 1000);

  if (now < graceEnd) {
    // Still within grace — show time remaining (calendar time, not working hours)
    const secsLeft = Math.max(0, Math.floor((graceEnd - now) / 60000));
    const h = Math.floor(secsLeft / 60), m = secsLeft % 60;
    const label = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: 'var(--c-success)' }}>
        <Timer size={9} /> {label}
      </span>
    );
  }

  const delayMins = workingMinsBetween(graceEnd, now);
  const color = delayMins === 0
    ? 'var(--text-muted)'
    : delayMins < 60  ? 'var(--gold-primary)'
    : delayMins < 240 ? 'var(--c-warning)'
    : 'var(--c-danger)';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color }}>
      <Timer size={9} /> {delayMins === 0 ? 'On time' : `${fmtWorkingTime(delayMins)} delay`}
    </span>
  );
};

// ─── Task Card ────────────────────────────────────────────────────────────────

const TaskCard = ({ task, selectedMainTab, currentUser, clickedReplyRows, onOpen, replyPanel, onOpenReply, onCloseReply, onSubmitReply, onAttachReply }) => {
  const p = getPriority(task.challengeLevel);
  const PIcon = p.Icon;
  const needsReply  = task.issueDelegatedTo === currentUser.name && !task.recommendation?.trim();
  const needsResolve = task.name === currentUser.name && task.resolvePlanned?.trim() && !task.resolveActual?.trim() && task.resolveLink;
  const isClicked   = clickedReplyRows.has(task.id);
  const isReplied   = !!task.recommendation?.trim();
  const isPanelOpen = replyPanel?.open;

  return (
    <div
      style={{
        borderRadius: 14, background: 'var(--bg-surface)',
        border: '1px solid var(--border)', borderLeft: `4px solid ${p.color}`,
        overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.18s ease, transform 0.18s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
      onClick={() => onOpen(task)}
    >
      {/* Body */}
      <div style={{ padding: '16px 16px 14px' }}>

        {/* Row 1: Ticket ID + badges */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 800, color: 'var(--gold-primary)', letterSpacing: '0.4px' }}>
              {task.ticketId}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: p.bg, color: p.color, border: `1px solid ${p.color}30` }}>
              <PIcon size={9} /> {p.label}
            </span>
            {isReplied && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: 'var(--c-success-bg)', color: 'var(--c-success)' }}>
                <CheckCircle2 size={9} /> Replied
              </span>
            )}
            {isClicked && !isReplied && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: 'var(--c-info-bg)', color: 'var(--c-info)' }}>
                <Send size={9} /> Sent
              </span>
            )}
          </div>
          <span style={{ flexShrink: 0 }}>
            {selectedMainTab === 'raisedOnYou'
              ? <DelayCounter timestamp={task.timestamp} />
              : <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>Your Ticket</span>}
          </span>
        </div>

        {/* Row 2: Challenge / Issue */}
        <p style={{
          fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.55, margin: '0 0 13px',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {task.challengeIssue || 'No description provided'}
        </p>

        {/* Row 3: Meta */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'center' }}>
          {task.name && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
              <User size={11} style={{ color: 'var(--text-muted)' }} /> {task.name}
            </span>
          )}
          {task.department && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
              <Building2 size={11} style={{ color: 'var(--text-muted)' }} /> {task.department}
            </span>
          )}
          {task.timestamp && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
              <Calendar size={11} /> {fmtDate(task.timestamp)}
            </span>
          )}
          {task.replyPlanned && !isReplied && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--c-warning)', fontWeight: 600 }}>
              <Clock size={11} /> Due {fmtDate(task.replyPlanned)}
            </span>
          )}
        </div>
      </div>

      {/* Footer action strip */}
      <div
        style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {needsReply && !isClicked && !isPanelOpen && (
            <>
              <button
                className="k-btn k-btn-danger"
                style={{ fontSize: 11, padding: '5px 11px' }}
                onClick={(e) => { e.stopPropagation(); onOpenReply(task.id); }}
                title="Type a quick reply inline — submits directly to the Google Form"
              >
                <Send size={11} /> Reply Inline
              </button>
              <button
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 11, fontWeight: 600, padding: '5px 11px', borderRadius: 8,
                  color: 'var(--text-muted)', background: 'none',
                  border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-2)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                onClick={(e) => { e.stopPropagation(); onAttachReply(task); }}
                title="Opens the Google Form in a new tab — use when you need to attach a file"
              >
                <ExternalLink size={11} /> With Attachment
              </button>
            </>
          )}
          {needsReply && isPanelOpen && !replyPanel?.submitted && (
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-warning)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <MessageSquare size={12} /> Writing reply…
            </span>
          )}
          {(isClicked || replyPanel?.submitted) && !isPanelOpen && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--c-success)' }}>
              <CheckCircle2 size={12} /> Reply Submitted
            </span>
          )}
          {needsResolve && (
            <button
              className="k-btn k-btn-gold"
              style={{ fontSize: 11, padding: '5px 11px' }}
              onClick={(e) => { e.stopPropagation(); window.open(task.resolveLink, '_blank'); }}
            >
              <ExternalLink size={11} /> Resolve
            </button>
          )}
        </div>
        <button
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 8, transition: 'color 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
          onClick={(e) => { e.stopPropagation(); onOpen(task); }}
        >
          Details <ChevronRight size={13} />
        </button>
        <button
          title="Ask AI about this ticket"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 11px', borderRadius: 99, cursor: 'pointer',
            background: 'linear-gradient(135deg, var(--green-primary) 0%, #1a4d2e 100%)',
            border: 'none', color: '#fff',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.3px',
            boxShadow: '0 2px 8px rgba(45,106,70,0.35)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(45,106,70,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(45,106,70,0.35)'; }}
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(new CustomEvent('ai-task-selected', {
              detail: {
                text: `Draft a reply for HT #${task.ticketId}: "${(task.challengeIssue || '').substring(0, 100)}" from ${task.name} (${task.department}), priority: ${task.challengeLevel}.`
              }
            }));
          }}
        >
          <KairaliAIIcon size={12} /> Ask AI
        </button>
      </div>

      {/* Inline reply panel — expands below footer */}
      {isPanelOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <ReplyPanel
            task={task}
            onSubmit={onSubmitReply}
            onCancel={() => onCloseReply(task.id)}
            submitting={replyPanel?.submitting}
            error={replyPanel?.error}
            submitted={replyPanel?.submitted}
          />
        </div>
      )}
    </div>
  );
};

// ─── Stat Pill ────────────────────────────────────────────────────────────────

const StatPill = ({ count, label, color, Icon }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 24, background: `${color}18`, border: `1px solid ${color}35`, flexShrink: 0 }}>
    <Icon size={13} style={{ color }} />
    <span style={{ fontSize: 14, fontWeight: 800, color, lineHeight: 1 }}>{count}</span>
    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', lineHeight: 1 }}>{label}</span>
  </div>
);

// ─── Inline Reply Panel ───────────────────────────────────────────────────────

const ReplyPanel = ({ task, onSubmit, onCancel, submitting, error, submitted }) => {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 60);
  }, []);

  const canSubmit = text.trim().length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(task, text.trim());
  };

  if (submitted) {
    return (
      <div style={{
        padding: '16px 18px',
        background: 'var(--c-success-bg)', borderTop: '1px solid var(--c-success)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <CheckCircle2 size={18} style={{ color: 'var(--c-success)', flexShrink: 0 }} />
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>Reply submitted to Google Form</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Your Recommendation/Solution has been recorded</p>
        </div>
      </div>
    );
  }

  const borderColor = error ? 'var(--c-danger)' : 'var(--border)';

  return (
    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', padding: '14px 16px' }}>

      {/* Label */}
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
        Recommendation / Solution
      </p>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Describe your recommendation or solution clearly…"
        rows={4}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 12px', borderRadius: 10, fontSize: 13,
          color: 'var(--text-primary)', background: 'var(--bg-surface-2)',
          border: `1px solid ${borderColor}`,
          outline: 'none', resize: 'vertical', lineHeight: 1.6,
          fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s',
          marginBottom: 8,
        }}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--gold-primary)';
          e.target.style.boxShadow = '0 0 0 3px var(--gold-dim)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = borderColor;
          e.target.style.boxShadow = 'none';
        }}
        disabled={submitting}
      />

      {/* Error */}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, background: 'var(--c-danger-bg)', border: '1px solid var(--c-danger)', marginBottom: 8 }}>
          <AlertCircle size={12} style={{ color: 'var(--c-danger)', flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: 'var(--c-danger)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        {submitting ? (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Loader size={11} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            Sending to Google Form… this takes a few seconds
          </span>
        ) : text.length > 0 ? (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 'auto' }}>
            {text.length} chars
          </span>
        ) : null}
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            color: 'var(--text-muted)', background: 'none',
            border: '1px solid var(--border)', cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.45 : 1,
          }}
          onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="k-btn k-btn-primary"
          style={{ fontSize: 12, padding: '6px 16px', opacity: submitting || text.trim().length > 0 ? 1 : 0.45, cursor: canSubmit ? 'pointer' : submitting ? 'wait' : 'not-allowed' }}
        >
          {submitting
            ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
            : <><Send size={12} /> Submit to Form</>}
        </button>
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ tab, mainTab }) => {
  const configs = {
    replyPending:   { Icon: CheckCircle2, color: 'var(--c-success)', title: 'All Caught Up!',         msg: 'No tickets are waiting for your reply.' },
    replyCompleted: { Icon: MessageSquare, color: 'var(--teal-primary)', title: 'Nothing Here Yet',   msg: 'No replied tickets to show.' },
    resolvePending: { Icon: Timer,         color: 'var(--c-warning)', title: 'Nothing Pending',       msg: 'No tickets awaiting resolution right now.' },
    completed:      { Icon: ShieldCheck,   color: 'var(--green-primary)', title: 'No Completed Tickets', msg: 'Completed tickets will appear here.' },
  };
  const c = configs[tab] ?? { Icon: Inbox, color: 'var(--text-muted)', title: 'No Tickets Found', msg: 'No tickets match the current filter.' };
  const { Icon, color, title, msg } = c;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '56px 24px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: 20, background: `${color}18`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <Icon size={28} style={{ color }} />
      </div>
      <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>{title}</p>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, maxWidth: 280, lineHeight: 1.5 }}>{msg}</p>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const HTTasks = ({ currentUser }) => {
  const [searchTerm, setSearchTerm]         = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();
  const [groupBy, setGroupBy]               = useState('none'); // 'none' | 'name' | 'department'
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // all collapsed by default
  const [selectedMainTab, setSelectedMainTab] = useState('raisedOnYou');
  const [selectedSubTab, setSelectedSubTab]   = useState('replyPending');
  const [selectedTask, setSelectedTask]       = useState(null);
  const [showModal, setShowModal]             = useState(false);
  const [clickedReplyRows, setClickedReplyRows] = useState(new Set());
  const [lastRefreshTime, setLastRefreshTime]   = useState(Date.now());
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  // replyPanelState: Map of taskId → { open, submitting, error, submitted }
  const [replyPanelState, setReplyPanelState] = useState({});
  // Optimistic patches: taskId → { recommendation } applied immediately on submit
  const [patchedTasks, setPatchedTasks] = useState({});

  const cleanHeaderName = (header) =>
    header.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

  const fetchHTData = useCallback(async () => {
    try {
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_HT;
      const sheetName     = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_HT;
      const params = new URLSearchParams({ sheetId: spreadsheetId, sheetName, range: 'A10:AZ' });
      const response = await fetch(`/api/sheets?${params}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const rows = data.values || [];
      if (rows.length === 0) return [];

      const headers = rows[0];
      const htTasks = rows.slice(1)
        .filter(row => row && row.length > 0)
        .map((row, index) => {
          const task = {};
          headers.forEach((header, colIndex) => {
            if (header && row[colIndex] !== undefined) task[cleanHeaderName(header)] = row[colIndex];
          });
          task.ticketId            = row[0]  || '';
          task.timestamp           = row[1]  || '';
          task.name                = row[2]  || '';
          task.emailId             = row[3]  || '';
          task.department          = row[4]  || '';
          task.challengeIssue      = row[5]  || '';
          task.challengeLevel      = row[6]  || '';
          task.issueDelegatedTo    = row[7]  || '';
          task.delegatedPersonEmail = row[8] || '';
          task.solution1           = row[9]  || '';
          task.solution2           = row[10] || '';
          task.solution3           = row[11] || '';
          task.attachment          = row[12] || '';
          task.problemSolvingLink  = row[14] || '';
          task.replyLink           = row[14] || '';
          task.newLink             = row[14] || '';
          task.replyPlanned        = row[18] || '';
          task.replyActual         = row[19] || '';
          task.replyTimeDelay      = row[20] || '';
          task.resolveTicketId     = row[21] || '';
          task.resolveRaisedBy     = row[22] || '';
          task.resolveAssignedTo   = row[23] || '';
          task.recommendation      = row[24] || '';
          task.resolveAttachment   = row[25] || '';
          task.resolveTimestamp    = row[26] || '';
          task.resolvePlanned      = row[27] || '';
          task.resolveActual       = row[28] || '';
          task.resolveTimeDelay    = row[29] || '';
          task.resolveLink         = row[30] || '';
          task.finalTicketId       = row[31] || '';
          task.finalDoer           = row[32] || '';
          task.problemResolved     = row[33] || '';
          task.remarks             = row[34] || '';
          task.finalTimestamp      = row[35] || '';
          task.id        = index + 1;
          task.rowNumber = index + 11;
          return task;
        })
        .filter(task => {
          const isRaisedByUser   = task.name === currentUser.name;
          const isDelegatedToUser = task.issueDelegatedTo === currentUser.name;
          const isAssignedToUser  = task.resolveAssignedTo === currentUser.name;
          return isRaisedByUser || isDelegatedToUser || isAssignedToUser;
        })
        .filter(task => task.ticketId || task.challengeIssue || task.name);

      console.log(`HT: ${htTasks.length} tasks for ${currentUser.name}`);
      return htTasks;
    } catch (error) {
      console.error('Error fetching HT data:', error);
      throw error;
    }
  }, [currentUser.name]);

  const { data: allTasks, loading, error, lastRefresh } = useCachedData('ht', currentUser, fetchHTData);

  // Merge optimistic patches so UI updates instantly on reply submit
  const displayTasks = allTasks.map(t => patchedTasks[t.id] ? { ...t, ...patchedTasks[t.id] } : t);

  useEffect(() => {
    if (lastRefresh && lastRefresh !== lastRefreshTime) {
      setClickedReplyRows(new Set());
      setPatchedTasks({});  // fresh data from sheet — clear optimistic patches
      setLastRefreshTime(lastRefresh);
    }
  }, [lastRefresh, lastRefreshTime]);

  const refresh = useCallback(async () => {
    setClickedReplyRows(new Set());
    setIsManualRefreshing(true);
    try {
      const { default: dataManager } = await import('../utils/DataManager');
      dataManager.cache.delete(`ht_${currentUser.name}`);
      const freshData = await fetchHTData();
      dataManager.setData('ht', currentUser.name, freshData);
      setLastRefreshTime(Date.now());
      return freshData;
    } catch (err) {
      console.error('Refresh error:', err);
      throw err;
    } finally {
      setIsManualRefreshing(false);
    }
  }, [fetchHTData, currentUser.name]);

  const isRefreshing = loading || isManualRefreshing;


  const openReplyPanel = useCallback((taskId) => {
    setReplyPanelState(prev => ({ ...prev, [taskId]: { open: true, submitting: false, error: null, submitted: false } }));
  }, []);

  const closeReplyPanel = useCallback((taskId) => {
    setReplyPanelState(prev => { const n = { ...prev }; delete n[taskId]; return n; });
  }, []);

  const submitReply = useCallback(async (task, replyText) => {
    const taskId = task.id;
    setReplyPanelState(prev => ({ ...prev, [taskId]: { ...prev[taskId], submitting: true, error: null } }));

    try {
      // 1. Submit to Google Form — wait for confirmation
      const formRes = await fetch('/api/ht-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId:             task.ticketId,
          name:                 task.name,
          emailId:              task.emailId,
          department:           task.department,
          challengeIssue:       task.challengeIssue,
          challengeLevel:       task.challengeLevel,
          issueDelegatedTo:     task.issueDelegatedTo,
          delegatedPersonEmail: task.delegatedPersonEmail,
          recommendation:       replyText,
        }),
      });
      const formJson = await formRes.json();
      if (!formRes.ok || !formJson.success) throw new Error(formJson.error || 'Form submission failed');

      // 2. Form confirmed — instantly remove from pending
      setPatchedTasks(prev => ({ ...prev, [taskId]: { recommendation: replyText } }));
      setClickedReplyRows(prev => { const s = new Set(prev); s.add(taskId); return s; });
      setReplyPanelState(prev => ({ ...prev, [taskId]: { ...prev[taskId], submitting: false, submitted: true } }));
      setTimeout(() => closeReplyPanel(taskId), 2000);

      // 3. Write to MasterHT sheet in background (col Y = Recommendation/Solution)
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_HT;
      const sheetName     = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_HT;
      fetch('/api/sheets', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadsheetId,
          range: `${sheetName}!Y${task.rowNumber}`,
          values: [[replyText]],
        }),
      }).catch(err => console.warn('HT sheet background write failed:', err));

    } catch (err) {
      setReplyPanelState(prev => ({ ...prev, [taskId]: { ...prev[taskId], submitting: false, error: err.message } }));
    }
  }, [closeReplyPanel]);

  // Open the full form in a new tab (for replies that need file attachments)
  const openFormForAttachment = useCallback((task) => {
    const link = task.replyLink || task.newLink || task.problemSolvingLink;
    if (link?.trim()) window.open(link, '_blank');
  }, []);

  const getTasksForMainTab = () => {
    const tasks = selectedMainTab === 'raisedOnYou'
      ? displayTasks.filter(t => t.issueDelegatedTo === currentUser.name)
      : displayTasks.filter(t => t.name === currentUser.name);
    return tasks.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const getSubTabs = () => selectedMainTab === 'raisedOnYou'
    ? [{ key: 'replyPending', label: 'Reply Pending' }, { key: 'replyCompleted', label: 'Replied' }]
    : [{ key: 'resolvePending', label: 'Resolve Pending' }, { key: 'replyPending', label: 'Reply Pending' }, { key: 'completed', label: 'Completed' }];

  const countForTab = (tabKey, tasks) => {
    if (selectedMainTab === 'raisedOnYou') {
      if (tabKey === 'replyPending')   return tasks.filter(t => !t.recommendation?.trim()).length;
      if (tabKey === 'replyCompleted') return tasks.filter(t => t.recommendation?.trim()).length;
    } else {
      if (tabKey === 'replyPending')   return tasks.filter(t => t.replyPlanned?.trim() && !t.replyActual?.trim()).length;
      if (tabKey === 'resolvePending') return tasks.filter(t => t.resolvePlanned?.trim() && !t.resolveActual?.trim()).length;
      if (tabKey === 'completed')      return tasks.filter(t => t.replyActual?.trim() && t.resolveActual?.trim()).length;
    }
    return 0;
  };

  const getFilteredTasks = () => {
    let tasks = getTasksForMainTab();
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      tasks = tasks.filter(t =>
        t.ticketId.toLowerCase().includes(q) ||
        t.challengeIssue.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.department.toLowerCase().includes(q)
      );
    }
    if (selectedMainTab === 'raisedOnYou') {
      if (selectedSubTab === 'replyPending')   tasks = tasks.filter(t => !t.recommendation?.trim());
      if (selectedSubTab === 'replyCompleted') tasks = tasks.filter(t => t.recommendation?.trim());
    } else {
      if (selectedSubTab === 'replyPending')   tasks = tasks.filter(t => t.replyPlanned?.trim() && !t.replyActual?.trim());
      if (selectedSubTab === 'resolvePending') tasks = tasks.filter(t => t.resolvePlanned?.trim() && !t.resolveActual?.trim());
      if (selectedSubTab === 'completed')      tasks = tasks.filter(t => t.replyActual?.trim() && t.resolveActual?.trim());
    }
    return tasks;
  };

  // Group filtered tasks when groupBy is active — must be before early returns (Rules of Hooks)
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') return null;
    // "By Person" groups by raiser on raisedOnYou tab, but by delegated-to on raisedByYou tab
    const key = groupBy === 'department' ? 'department'
      : selectedMainTab === 'raisedByYou' ? 'issueDelegatedTo'
      : 'name';
    const groups = {};
    const source = getFilteredTasks();
    source.forEach(t => {
      const label = t[key] || 'Unknown';
      if (!groups[label]) groups[label] = [];
      groups[label].push(t);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTasks, groupBy, selectedMainTab, selectedSubTab, searchTerm]);

  // ─── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading">
          <div className="k-loading-spinner" />
          <p className="k-loading-text">Loading HT Tasks…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-page">
        <div className="k-card k-error">
          <div className="k-error-icon"><AlertTriangle size={28} /></div>
          <p className="k-error-title">Error Loading HT Tasks</p>
          <p className="k-error-msg">{error}</p>
          <button onClick={refresh} className="k-btn k-btn-primary" style={{ marginTop: 8 }}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const mainTabTasks = getTasksForMainTab();
  const filteredTasks = getFilteredTasks();
  const subTabs = getSubTabs();

  // Quick stats for the currently visible main tab
  const pendingCount  = selectedMainTab === 'raisedOnYou'
    ? mainTabTasks.filter(t => !t.recommendation?.trim()).length
    : mainTabTasks.filter(t => t.resolvePlanned?.trim() && !t.resolveActual?.trim()).length;
  const doneCount = selectedMainTab === 'raisedOnYou'
    ? mainTabTasks.filter(t => t.recommendation?.trim()).length
    : mainTabTasks.filter(t => t.resolveActual?.trim()).length;

  // Sub-tab active color
  const subTabColor = (key) => {
    if (key === 'replyPending' || key === 'resolvePending') return 'var(--c-danger)';
    if (key === 'completed' || key === 'replyCompleted')    return 'var(--c-success)';
    return 'var(--gold-primary)';
  };

  return (
    <div className="k-page">


      {/* ── Header card: main tabs + stats ──────────────────────────────────── */}
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>

        {/* Main tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          {[
            { key: 'raisedOnYou', label: 'Raised On You', count: allTasks.filter(t => t.issueDelegatedTo === currentUser.name).length },
            { key: 'raisedByYou', label: 'Raised By You', count: allTasks.filter(t => t.name === currentUser.name).length },
          ].map(tab => {
            const active = selectedMainTab === tab.key;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={active}
                title={active ? `Showing ${tab.label}` : `Switch to ${tab.label}`}
                onClick={() => {
                  setSelectedMainTab(tab.key);
                  setSelectedSubTab(tab.key === 'raisedOnYou' ? 'replyPending' : 'resolvePending');
                  setSearchTerm('');
                }}
                style={{
                  flex: 1, padding: '14px 20px', fontSize: 13,
                  // Note: `border: none` must come BEFORE the per-side borders —
                  // the shorthand resets them, so listing it after silently
                  // removed the gold underline that marks the selected tab.
                  // One slim underline only: a top rule as well read as a
                  // heavy bracket around the selected tab.
                  border: 'none',
                  borderBottom: `2px solid ${active ? 'var(--gold-primary)' : 'transparent'}`,
                  marginBottom: -1,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: '0.01em',
                  color: active ? 'var(--gold-primary)' : 'var(--text-muted)',
                  background: active ? 'var(--bg-surface)' : 'var(--bg-surface-2)',
                  cursor: 'pointer', transition: 'all 0.18s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {tab.label}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
                  fontSize: 10, fontWeight: 800,
                  background: active ? 'var(--gold-dim)' : 'var(--bg-surface-3)',
                  color: active ? 'var(--gold-primary)' : 'var(--text-muted)',
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Stat pills strip */}
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: 'var(--bg-surface-2)', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <StatPill
            count={pendingCount}
            label={selectedMainTab === 'raisedOnYou' ? 'Reply Pending' : 'Resolve Pending'}
            color="var(--c-danger)"
            Icon={AlertCircle}
          />
          <StatPill
            count={doneCount}
            label={selectedMainTab === 'raisedOnYou' ? 'Replied' : 'Resolved'}
            color="var(--c-success)"
            Icon={CheckCircle2}
          />
          <StatPill
            count={mainTabTasks.length}
            label="Total"
            color="var(--text-muted)"
            Icon={Inbox}
          />
        </div>

      </div>

      {/* ── Sub-tabs + search: pinned while the list scrolls ──────────────── */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`} style={{ padding: 0, overflow: 'hidden' }}>
        {/* Sub-tabs */}
        <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
          {subTabs.map(tab => {
            const active = selectedSubTab === tab.key;
            const color  = subTabColor(tab.key);
            const count  = countForTab(tab.key, mainTabTasks);
            return (
              <button
                key={tab.key}
                onClick={() => setSelectedSubTab(tab.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '11px 4px', marginRight: 20,
                  fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  color: active ? color : 'var(--text-muted)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: `2px solid ${active ? color : 'transparent'}`,
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                  fontSize: 10, fontWeight: 800,
                  background: active ? `${color}20` : 'var(--bg-surface-2)',
                  color: active ? color : 'var(--text-muted)',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              type="text"
              className="k-search"
              placeholder="Search by ticket ID, issue, person…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}
            >
              <X size={12} /> Clear
            </button>
          )}
          {/* Group-by toggle */}
          <div style={{ display: 'flex', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', flexShrink: 0 }}>
            {[
              { key: 'none',       label: 'List' },
              { key: 'name',       label: 'By Person' },
              { key: 'department', label: 'By Dept' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setGroupBy(opt.key)}
                style={{
                  padding: '5px 10px', fontSize: 11, fontWeight: 600,
                  background: groupBy === opt.key ? 'var(--gold-primary)' : 'var(--bg-surface-2)',
                  color: groupBy === opt.key ? '#000' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={refresh}
            disabled={isRefreshing}
            title="Refresh"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'var(--bg-surface-2)', border: '1px solid var(--border)',
              cursor: isRefreshing ? 'not-allowed' : 'pointer', color: 'var(--text-muted)',
            }}
          >
            <RefreshCw size={13} style={{ transition: 'transform 0.6s', transform: isRefreshing ? 'rotate(360deg)' : 'none' }} />
          </button>
        </div>
      </div>

      {/* ── Task list ───────────────────────────────────────────────────────── */}
      {filteredTasks.length === 0 ? (
        <div className="k-card" style={{ overflow: 'hidden' }}>
          <EmptyState tab={selectedSubTab} mainTab={selectedMainTab} />
        </div>
      ) : groupedTasks ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groupedTasks.map(([groupLabel, groupItems]) => {
            const isCollapsed = !expandedGroups.has(groupLabel);
            const toggle = () => setExpandedGroups(prev => {
              const next = new Set(prev);
              next.has(groupLabel) ? next.delete(groupLabel) : next.add(groupLabel);
              return next;
            });
            return (
              <div key={groupLabel} className="k-card" style={{ overflow: 'hidden', padding: 0 }}>
                {/* Group header — click to collapse/expand */}
                <button
                  onClick={toggle}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: 'var(--bg-surface-2)',
                    border: 'none', borderBottom: isCollapsed ? 'none' : '1px solid var(--border)',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', flex: 1, minWidth: 0, truncate: true }}>
                    {groupLabel}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
                    fontSize: 10, fontWeight: 800,
                    background: 'var(--bg-surface)', color: 'var(--text-muted)',
                    border: '1px solid var(--border)',
                  }}>
                    {groupItems.length}
                  </span>
                </button>
                {/* Group items */}
                {!isCollapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {groupItems.map((task, i) => (
                      <div key={i} style={{ borderBottom: i < groupItems.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <TaskCard
                          task={task}
                          selectedMainTab={selectedMainTab}
                          currentUser={currentUser}
                          clickedReplyRows={clickedReplyRows}
                          replyPanel={replyPanelState[task.id]}
                          onOpenReply={openReplyPanel}
                          onCloseReply={closeReplyPanel}
                          onSubmitReply={submitReply}
                          onAttachReply={openFormForAttachment}
                          onOpen={(t) => { setSelectedTask(t); setShowModal(true); }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredTasks.map((task, i) => (
            <TaskCard
              key={i}
              task={task}
              selectedMainTab={selectedMainTab}
              currentUser={currentUser}
              clickedReplyRows={clickedReplyRows}
              replyPanel={replyPanelState[task.id]}
              onOpenReply={openReplyPanel}
              onCloseReply={closeReplyPanel}
              onSubmitReply={submitReply}
              onAttachReply={openFormForAttachment}
              onOpen={(t) => { setSelectedTask(t); setShowModal(true); }}
            />
          ))}
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      {showModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => { setShowModal(false); setSelectedTask(null); }}
          currentUser={currentUser}
          clickedReplyRows={clickedReplyRows}
          replyPanel={replyPanelState[selectedTask.id]}
          onOpenReply={openReplyPanel}
          onCloseReply={closeReplyPanel}
          onSubmitReply={submitReply}
          onAttachReply={openFormForAttachment}
        />
      )}
    </div>
  );
};

export default HTTasks;
